import { createSlice, Slice } from '@reduxjs/toolkit';
import { initPaginatedListData } from 'modules/shared/constants';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import {
	formateApiDateValue,
	formateNullableEnumValue,
} from 'modules/utilities/Helpers';
import { RootStateOrAny } from 'react-redux';
import {
	IShopModule,
	IShopModuleBase,
	IShopModuleListItem,
} from '../interfaces/shop-module';
import { ITag } from '../interfaces/tag';

export const SHOP_KEY_IN_STORE = 'shop';

export interface IShopState {
	isLoading: boolean;

	shopModules: IPaginatedData<IShopModuleListItem>;
	searchShopModulesAutoComplete: {
		isLoading: boolean;
		result: IShopModuleBase[];
	};

	deleteShopModule: IReqStatus;
	createShopModule: IReqStatus;
	updateShopModule: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IShopModule;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
	newShopModuleOrder: {
		order: number;
		isLoading: boolean;
	};
	tags: {
		isLoading: boolean;
		searchTagsAutoComplete: {
			isLoading: boolean;
			result: ITag[];
		};
		createTag: IReqStatus & { tag?: ITag };
		updateTag: IReqStatus & { tag?: ITag };
		deleteTag: IReqStatus;
	};
}

const initialState: IShopState = {
	isLoading: false,

	shopModules: initPaginatedListData,

	searchShopModulesAutoComplete: {
		isLoading: false,
		result: [],
	},
	deleteShopModule: {
		error: false,
		success: false,
	},
	createShopModule: {
		error: false,
		success: false,
	},
	updateShopModule: {
		itemToUpdate: {
			isLoading: false,
			status: {
				error: false,
				success: false,
			},
		},
		status: {
			error: false,
			success: false,
		},
	},
	newShopModuleOrder: {
		order: 1,
		isLoading: false,
	},
	tags: {
		isLoading: false,
		searchTagsAutoComplete: {
			isLoading: false,
			result: [],
		},
		createTag: {
			error: false,
			success: false,
		},
		updateTag: {
			error: false,
			success: false,
		},
		deleteTag: {
			error: false,
			success: false,
		},
	},
};

const shopSlice: Slice = createSlice({
	name: SHOP_KEY_IN_STORE,
	initialState,
	reducers: {
		resetShopData: () => {
			return initialState;
		},

		getShopModules: (state: IShopState) => {
			state.isLoading = true;
		},
		getShopModulesSuccess: (state: IShopState, action) => {
			state.isLoading = false;
			state.shopModules = action.payload;
		},
		getShopModulesFailure: (state: IShopState) => {
			state.isLoading = false;
			state.shopModules = initialState.shopModules;
		},

		searchShopModules: (state: IShopState) => {
			state.searchShopModulesAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchShopModulesResult: (state: IShopState, action) => {
			state.searchShopModulesAutoComplete = {
				isLoading: false,
				result: action.payload.shopModules,
			};
		},
		resetSearchShopModules: (state: IShopState) => {
			state.searchShopModulesAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		deleteShopModule: (state: IShopState) => {
			state.isLoading = true;
		},
		deleteShopModuleSuccess: (state: IShopState) => {
			state.isLoading = false;
			state.deleteShopModule = {
				error: false,
				success: true,
			};
		},
		deleteShopModuleFailure: (state: IShopState) => {
			state.isLoading = false;
			state.deleteShopModule = {
				error: true,
				success: false,
			};
		},
		resetDeleteShopModule: (state: IShopState) => {
			state.deleteShopModule = {
				error: false,
				success: false,
			};
		},

		createShopModule: (state: IShopState) => {
			state.isLoading = true;
		},
		createShopModuleSuccess: (state: IShopState) => {
			state.createShopModule = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createShopModuleFailure: (state: IShopState) => {
			state.createShopModule = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getShopModuleToUpdate: (state: IShopState) => {
			state.updateShopModule.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getShopModuleToUpdateSuccess: (state: IShopState, action) => {
			const { shopModule } = action.payload;
			state.updateShopModule.itemToUpdate = {
				isLoading: false,
				data: {
					...shopModule,
					showFrom: formateApiDateValue(shopModule.showFrom),
					showTo: formateApiDateValue(shopModule.showTo),
					discountFrom: formateApiDateValue(shopModule.discountFrom),
					discountTo: formateApiDateValue(shopModule.discountTo),
					showAfterCompleteRegion: formateNullableEnumValue(
						shopModule.showAfterCompleteRegion,
					),
				},
				status: {
					success: true,
					error: false,
				},
			};
		},
		getShopModuleToUpdateFailure: (state: IShopState) => {
			state.updateShopModule.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateShopModule: (state: IShopState) => {
			state.isLoading = true;
		},
		updateShopModuleSuccess: (state: IShopState) => {
			state.updateShopModule.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateShopModuleFailure: (state: IShopState) => {
			state.updateShopModule.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getNewShopModuleOrder: (state: IShopState) => {
			state.newShopModuleOrder.isLoading = true;
		},
		setNewShopModuleOrder: (state: IShopState, action) => {
			state.newShopModuleOrder = {
				isLoading: false,
				order: action.payload.order,
			};
		},

		searchTags: (state: IShopState) => {
			state.tags.searchTagsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchTagsResult: (state: IShopState, action) => {
			state.tags.searchTagsAutoComplete = {
				isLoading: false,
				result: action.payload.tags,
			};
		},
		resetSearchTags: (state: IShopState) => {
			state.tags.searchTagsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		createTag: (state: IShopState) => {
			state.tags.isLoading = true;
		},
		createTagSuccess: (state: IShopState, action) => {
			state.tags.createTag = {
				success: true,
				error: false,
				tag: action.payload.data,
			};
			state.tags.isLoading = false;
		},

		createTagFailure: (state: IShopState) => {
			state.tags.createTag = {
				success: false,
				error: true,
			};
			state.tags.isLoading = false;
		},

		resetCreateTag: (state: IShopState) => {
			state.tags.createTag = {
				...state.tags.createTag,
				success: false,
				error: false,
			};
			state.tags.isLoading = false;
		},

		updateTag: (state: IShopState) => {
			state.tags.isLoading = true;
		},
		updateTagSuccess: (state: IShopState, action) => {
			state.tags.updateTag = {
				success: true,
				error: false,
				tag: action.payload.data,
			};
			state.tags.isLoading = false;
		},
		updateTagFailure: (state: IShopState) => {
			state.tags.updateTag = {
				success: false,
				error: true,
			};
			state.tags.isLoading = false;
		},

		resetUpdateTag: (state: IShopState) => {
			state.tags.updateTag = {
				...state.tags.updateTag,
				success: false,
				error: false,
			};
			state.tags.isLoading = false;
		},

		deleteTag: (state: IShopState) => {
			state.tags.isLoading = true;
		},
		deleteTagSuccess: (state: IShopState) => {
			state.tags.deleteTag = {
				success: true,
				error: false,
			};
			state.tags.isLoading = false;
		},
		deleteTagFailure: (state: IShopState) => {
			state.tags.deleteTag = {
				success: false,
				error: true,
			};
			state.tags.isLoading = false;
		},

		resetDeleteTag: (state: IShopState) => {
			state.tags.deleteTag = {
				success: false,
				error: false,
			};
			state.tags.isLoading = false;
		},
	},
});

export const {
	resetShopData,

	getShopModules,
	getShopModulesSuccess,
	getShopModulesFailure,

	searchShopModules,
	setSearchShopModulesResult,
	resetSearchShopModules,

	deleteShopModule,
	deleteShopModuleSuccess,
	deleteShopModuleFailure,
	resetDeleteShopModule,

	createShopModule,
	createShopModuleSuccess,
	createShopModuleFailure,

	getShopModuleToUpdate,
	getShopModuleToUpdateSuccess,
	getShopModuleToUpdateFailure,

	updateShopModule,
	updateShopModuleSuccess,
	updateShopModuleFailure,
	getNewShopModuleOrder,
	setNewShopModuleOrder,

	searchTags,
	setSearchTagsResult,
	resetSearchTags,

	createTag,
	createTagSuccess,
	createTagFailure,

	updateTag,
	updateTagSuccess,
	updateTagFailure,

	deleteTag,
	deleteTagSuccess,
	deleteTagFailure,

	resetCreateTag,
	resetUpdateTag,
	resetDeleteTag,
} = shopSlice.actions;

export const ShopStateSelector = (state: RootStateOrAny): IShopState =>
	state[SHOP_KEY_IN_STORE];

export default shopSlice;
